/**
 * usePrefetchNextQuestions Hook - Phase 4a: Smart Prefetching
 *
 * Intelligently preloads upcoming questions based on:
 * - Current question index
 * - Total questions in test
 * - Network speed detection
 *
 * Preloads next 2-5 questions ahead of current position
 */

import { useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { preloadAnimationsForQuestions } from '../components/PracticeTest/QuestionAnimationPlayer';

interface PrefetchConfig {
  currentIndex: number;
  totalQuestions: number;
  questions: Array<any>;
  enabled?: boolean;
}

/**
 * Detect network speed and determine prefetch batch size
 * Returns number of questions ahead to prefetch
 */
function getPrefetchBatchSize(): number {
  // If navigator.connection API available, use it
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;
    const effectiveType = conn?.effectiveType || 'unknown';

    switch (effectiveType) {
      case '4g':
        return 5; // Prefetch 5 questions ahead on fast connection
      case '3g':
        return 3; // Prefetch 3 questions ahead on medium
      case '2g':
      case 'slow-2g':
        return 1; // Prefetch only 1 question on slow connection
      default:
        return 3; // Default to 3
    }
  }

  // Fallback to default if API not available
  return 3;
}

/**
 * Hook to smartly prefetch next questions
 */
export function usePrefetchNextQuestions(config: PrefetchConfig): void {
  const { currentIndex, totalQuestions, questions, enabled = true } = config;

  const prefetchNext = useCallback(() => {
    if (!enabled || !questions || questions.length === 0) return;

    // Determine how many questions ahead to prefetch
    const batchSize = getPrefetchBatchSize();

    // Calculate range of questions to prefetch
    const startIndex = currentIndex + 1;
    const endIndex = Math.min(startIndex + batchSize, totalQuestions);

    if (startIndex >= totalQuestions) {
      // No more questions to prefetch
      return;
    }

    // Extract questions to prefetch
    const questionsToPreload = questions.slice(startIndex, endIndex);

    if (questionsToPreload.length === 0) return;

    logger.debug(`Prefetching ${questionsToPreload.length} questions (${startIndex}-${endIndex - 1})`, {
      currentIndex,
      batchSize,
      networkType: (navigator as any).connection?.effectiveType || 'unknown'
    });

    // Use requestIdleCallback to avoid blocking main thread
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        preloadAnimationsForQuestions(questionsToPreload, { maxImmediate: 1, staggerMs: 120 });
      });
    } else {
      // Fallback to setTimeout
      setTimeout(() => {
        preloadAnimationsForQuestions(questionsToPreload, { maxImmediate: 1, staggerMs: 120 });
      }, 100);
    }
  }, [currentIndex, totalQuestions, questions, enabled]);

  // Prefetch when question index changes
  useEffect(() => {
    prefetchNext();
  }, [currentIndex, prefetchNext]);
}

/**
 * Hook to detect network speed changes and react
 * Useful for adjusting quality on-the-fly
 */
export function useNetworkAwareness(): {
  effectiveType: string;
  downlink?: number;
  rtt?: number;
  isSaveData: boolean;
} {
  const conn = (navigator as any).connection;

  return {
    effectiveType: conn?.effectiveType || '4g',
    downlink: conn?.downlink,
    rtt: conn?.rtt,
    isSaveData: conn?.saveData || false
  };
}
